import { closestCenter, DndContext, DragEndEvent, UniqueIdentifier, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useEffect, useState } from 'react';

import { CustomPointerSensor } from './Overrides';
import { SortableItem, SortableListItem } from './SortableItem';

interface Props<TID extends UniqueIdentifier, T extends SortableListItem<TID>> {
  list: T[];
  onReorder: (itemId: T['id'], previousItem?: T, nextItem?: T) => void;
}

export const SortableList = <TID extends UniqueIdentifier, T extends SortableListItem<TID>>({
  list,
  onReorder,
}: Props<TID, T>) => {
  const [items, setItems] = useState<T[]>(list);
  const sensors = useSensors(useSensor(CustomPointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const updatedItems = arrayMove(items, oldIndex, newIndex);

      setItems(updatedItems);
      onReorder(updatedItems[newIndex].id, updatedItems[newIndex - 1], updatedItems[newIndex + 1]);
    }
  };

  useEffect(() => {
    setItems(list);
  }, [list]);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((item) => (
          <SortableItem key={item.id} id={item.id} content={item.content} />
        ))}
      </SortableContext>
    </DndContext>
  );
};
